/**
 * GTM script injection and dataLayer management.
 *
 * Ported from legacy `apps/web/src/utils/gtm/browser.js`.
 * Injects the Google Tag Manager script into the page and provides
 * functions for pushing data to the GTM dataLayer.
 */

import { AppEnvironment } from '~/configuration';
import { clientLogger } from '~/lib/logger';

const DEFAULT_SCRIPT_SRC = 'https://www.googletagmanager.com/gtm.js';
const DEFAULT_DATA_LAYER_NAME = 'dataLayer';

export type GtmConfig = {
  /**
   * Every container the page runs, in load order. They share one dataLayer:
   * GTM supports several containers on a page, and each reads the same
   * queue, so the params and the `gtm.js` event are pushed once for all of
   * them rather than once per container (which would fire every page-view
   * trigger once per container, in every container).
   */
  containerIds: string[];
  nonce: string;
  params?: Record<string, string>;
  companyId?: string;
  dataLayerName?: string;
  preview?: string;
  auth?: string;
  execution?: 'async' | 'defer';
};

let initializedDataLayerName: string | undefined;

function isScriptLoaded(containerId: string): boolean {
  const scriptSrcEscaped = DEFAULT_SCRIPT_SRC.replace(
    /^https?:\/\//,
    ''
  ).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(scriptSrcEscaped + '.*[?&]id=' + containerId);
  const scripts = document.querySelectorAll('script[src]');
  return Array.from(scripts).some((script) =>
    regex.test((script as HTMLScriptElement).src ?? '')
  );
}

/**
 * Injects one GTM script tag per container and initializes the dataLayer.
 */
export function initGtm(config: GtmConfig): void {
  const {
    containerIds,
    nonce,
    params,
    companyId,
    dataLayerName = DEFAULT_DATA_LAYER_NAME,
    preview,
    auth,
    execution = 'async',
  } = config;

  if (AppEnvironment === 'local') {
    clientLogger.debug(
      '[plugins/gtm] gtm is not initialized in local environments. The server side container calls do not work locally.'
    );
    return;
  }

  const ids = [...new Set(containerIds.filter(Boolean))];
  if (ids.length === 0) {
    clientLogger.warn(
      '[plugins/gtm] No containerId provided, skipping GTM init'
    );
    return;
  }

  if (preview && !auth) {
    clientLogger.warn(
      '[plugins/gtm] Preview mode requires both preview and auth parameters'
    );
    return;
  }

  const toLoad = ids.filter((id) => !isScriptLoaded(id));
  if (toLoad.length === 0) {
    return;
  }

  const dl =
    ((window as unknown as Record<string, unknown>)[dataLayerName] as Record<
      string,
      unknown
    >[]) ?? [];
  (window as unknown as Record<string, unknown>)[dataLayerName] = dl;

  // Push params to dataLayer
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      dl.push({ [k]: v });
    }
  }

  if (companyId) {
    dl.push({ company_id: companyId });
  }

  dl.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  // Build script URL
  const dlParam = dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : '';
  const previewParam = preview
    ? `&gtm_preview=${preview}&gtm_auth=${auth}&gtm_cookies_win=x`
    : '';

  for (const containerId of toLoad) {
    dl.push({ config: containerId, send_page_view: false });

    const script = document.createElement('script');
    if (execution === 'async') {
      script.async = true;
    } else if (execution === 'defer') {
      script.defer = true;
    }
    script.src = `${DEFAULT_SCRIPT_SRC}?id=${containerId}${dlParam}${previewParam}`;
    script.setAttribute('nonce', nonce);

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    }
  }

  initializedDataLayerName = dataLayerName;
}

/**
 * Pushes data to the GTM dataLayer.
 */
export function pushToDataLayer(
  data: Record<string, unknown>,
  dataLayerName = DEFAULT_DATA_LAYER_NAME
): void {
  const dl = (window as unknown as Record<string, unknown>)[dataLayerName] as
    Record<string, unknown>[] | undefined;
  if (dl) {
    dl.push(data);
  }
}

/**
 * Returns whether GTM has been loaded and the dataLayer is active.
 */
export function isGtmLoaded(): boolean {
  if (!initializedDataLayerName) return false;

  const dl = (window as unknown as Record<string, unknown>)[
    initializedDataLayerName
  ] as unknown[] | undefined;
  if (!dl) return false;

  // GTM replaces Array.prototype.push with its own implementation
  return Array.prototype.push !== dl.push;
}
